import { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { CoachRow, won } from './tabData';
import CoachReportSheet from './CoachReportSheet';
import { NO_CENTER_AVG } from './coachAnalysis';
import { composeReport } from './reportComposer';
import { useOpsContext } from './useOpsContext';
import {
    useOpsBriefingAccess,
    useOpsCoachesQuery,
} from '../../services/useOpsBriefingService';

dayjs.locale('ko');

/**
 * 강사 성과 리포트 — 상시 버튼에서 여는 팝업.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 팝업인가
 * ─────────────────────────────────────────────────────────────
 * 리포트는 특정 탭에 속한 기능이 아닙니다. 원장이 어느 화면을 보다가도
 * "이번 달 리포트 뽑아야지" 하고 떠올리는 일이라, 화면 안에 칸을 두면
 * 그 화면에 있을 때만 생각납니다.
 *
 * 그래서 상단에 상시 버튼을 두고 팝업으로 엽니다.
 * 안에서 달을 고르고 강사를 고르면 바로 미리보기 + 다운로드로 이어집니다.
 */

const BORDER = '#E3EAF3';
const NAVY = '#1A305D';

/** 리포트를 뽑을 수 있는 가장 오래된 달 */
const MAX_PAST_MONTHS = 12;

type Props = { onClose: () => void };

const CoachReportModal = ({ onClose }: Props) => {
    const ctx = useOpsContext();
    const [month, setMonth] = useState<string>(() => dayjs().format('YYYY-MM'));
    const [picked, setPicked] = useState<CoachRow | null>(null);

    /*
     * 노출 게이트 — 강사 조회도 서버 게이트 안이라 닫힌 센터에서는 404 다.
     * 이 팝업은 운영 브리핑 밖(센터 리포트)에서도 열리므로 여기서도 판정한다.
     */
    const { allowed } = useOpsBriefingAccess(ctx);

    /*
     * 목록을 그리는 데 필요한 것만 여기서 부릅니다.
     * 회원 명부·지시 이행은 리포트를 실제로 열 때 CoachReportSheet 가 부릅니다 —
     * 팝업을 열자마자 다 부르면 강사를 고르기도 전에 기다리게 됩니다.
     */
    const coachesQuery = useOpsCoachesQuery(ctx, month, ctx.ready && allowed);

    const coaches = coachesQuery.data?.coaches ?? [];
    /*
     * 비교 기준선 — 목 상수로 메우지 않는다.
     * 이 값은 목록의 '표본 적음' 배지 판단에 쓰인다.
     * 서버 평균이 없는데 상수를 끼우면 지어낸 기준으로 매긴 등급이 PDF 에 인쇄된다.
     */
    const centerAvg = coachesQuery.data?.centerAvg ?? NO_CENTER_AVG;

    const current = dayjs(`${month}-01`);
    const thisMonth = dayjs().startOf('month');
    const oldest = thisMonth.subtract(MAX_PAST_MONTHS, 'month');
    const canPrev = current.isAfter(oldest, 'month');
    const canNext = current.isBefore(thisMonth, 'month');

    const move = (delta: number) => {
        const next = current.add(delta, 'month');
        if (next.isAfter(thisMonth, 'month') || next.isBefore(oldest, 'month')) return;
        setMonth(next.format('YYYY-MM'));
        // 달이 바뀌면 열어 둔 리포트는 그 달 것이 아니다. 닫는다.
        setPicked(null);
    };

    /** 담당 미사용 선수금이 큰 순 — 먼저 챙길 강사가 앞에 온다 */
    const ordered = [...coaches].sort((a, b) => b.portfolioValue - a.portfolioValue);

    /* 게이트가 닫힌 센터에서는 조용히 아무것도 그리지 않는다 (버튼도 같이 숨는다) */
    if (!allowed) return null;

    return (
        <>
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ background: 'rgba(12,22,40,0.55)', zIndex: 2400 }}
            onClick={onClose}
            role="presentation"
        >
            <div
                className="flex flex-col rounded-[18px]"
                style={{
                    width: 760,
                    maxHeight: '84vh',
                    background: '#FFFFFF',
                    boxShadow: '0 30px 80px rgba(12,22,40,0.35)',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
                role="presentation"
            >
                {/* 헤더 */}
                <div
                    className="flex items-center justify-between"
                    style={{ background: NAVY, padding: '20px 26px' }}
                >
                    <div className="flex flex-col">
                        <span style={{ fontSize: 19, fontWeight: 800, color: '#FFFFFF' }}>
                            강사 성과 리포트
                        </span>
                        <span style={{ fontSize: 12.5, color: '#9DB4D8', marginTop: 3 }}>
                            면담용 A4 리포트 · 강사를 누르면 미리보기와 PDF 저장으로 이어집니다
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="닫기"
                        className="rounded-[9px]"
                        style={{
                            width: 36,
                            height: 36,
                            background: 'rgba(255,255,255,0.14)',
                            border: '1px solid rgba(255,255,255,0.24)',
                            color: '#FFFFFF',
                            fontSize: 17,
                            cursor: 'pointer',
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* 달 선택 */}
                <div
                    className="flex items-center justify-between"
                    style={{ padding: '14px 26px', borderBottom: `1px solid ${BORDER}`, background: '#F7F9FC' }}
                >
                    <span style={{ fontSize: 13.5, color: '#5B6B7F' }}>
                        어느 달 리포트를 뽑으시겠어요?
                    </span>
                    <div
                        className="flex items-center rounded-[10px]"
                        style={{ border: `1px solid ${BORDER}`, background: '#FFFFFF' }}
                    >
                        <button
                            type="button"
                            onClick={() => move(-1)}
                            disabled={!canPrev}
                            aria-label="이전 달"
                            style={{
                                width: 36, height: 38, background: 'transparent', border: 'none',
                                color: canPrev ? '#425063' : '#C9D3E0', fontSize: 16,
                                cursor: canPrev ? 'pointer' : 'not-allowed',
                            }}
                        >
                            ‹
                        </button>
                        <span
                            style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E', minWidth: 104, textAlign: 'center' }}
                        >
                            {current.format('YYYY년 M월')}
                        </span>
                        <button
                            type="button"
                            onClick={() => move(1)}
                            disabled={!canNext}
                            aria-label="다음 달"
                            style={{
                                width: 36, height: 38, background: 'transparent', border: 'none',
                                color: canNext ? '#425063' : '#C9D3E0', fontSize: 16,
                                cursor: canNext ? 'pointer' : 'not-allowed',
                            }}
                        >
                            ›
                        </button>
                    </div>
                </div>

                {/* 이번 달은 아직 안 끝났다. 안 밝히면 월말 실적으로 오해한다 */}
                {current.isSame(thisMonth, 'month') && (
                    <div style={{ padding: '10px 26px', background: '#FEF9E7' }}>
                        <span style={{ fontSize: 12.5, color: '#8A6D1F' }}>
                            이번 달은 아직 진행 중입니다. 확정된 실적은 달이 끝난 뒤에 보세요.
                        </span>
                    </div>
                )}

                {/* 강사 목록 */}
                <div style={{ padding: '16px 26px 22px', overflowY: 'auto' }}>
                    {coachesQuery.isLoading ? (
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            강사 목록을 불러오는 중입니다…
                        </span>
                    ) : coachesQuery.isError ? (
                        <span style={{ fontSize: 13.5, color: '#C22D38' }}>
                            강사 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                        </span>
                    ) : ordered.length === 0 ? (
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            {current.format('M월')}에 활동한 강사가 없습니다.
                        </span>
                    ) : (
                        <div className="flex flex-col" style={{ gap: 8 }}>
                            {ordered.map((c) => {
                                // 표본이 부족한 강사는 리포트 구성이 달라진다. 미리 알려준다.
                                const plan = composeReport(c, centerAvg, coaches);
                                const thin = plan.maturity.level !== 'RICH';
                                return (
                                    <button
                                        key={c.coachId}
                                        type="button"
                                        onClick={() => setPicked(c)}
                                        className="flex items-center rounded-[11px]"
                                        style={{
                                            border: `1px solid ${BORDER}`,
                                            background: '#FFFFFF',
                                            padding: '13px 16px',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            gap: 12,
                                        }}
                                    >
                                        <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
                                            <div className="flex items-center" style={{ gap: 7 }}>
                                                <span style={{ fontSize: 15.5, fontWeight: 700, color: '#16202E' }}>
                                                    {c.name} 강사
                                                </span>
                                                {thin && (
                                                    <span
                                                        className="rounded-[4px]"
                                                        title={plan.maturity.note}
                                                        style={{
                                                            background: '#FEF3C7', color: '#8A6D1F',
                                                            fontSize: 10.5, fontWeight: 700, padding: '1px 5px',
                                                        }}
                                                    >
                                                        표본 적음
                                                    </span>
                                                )}
                                            </div>
                                            {/*
                                              🔴 「담당」이 아니라 「활성」이다 (2026-09-03 대표 QA ②).
                                                 `activeMembers` 는 지금 활성 수강권을 가진 회원 수라
                                                 배정된 담당 회원 수가 아니다. 브리핑 강사 카드가
                                                 같은 이름으로 배정 전체(만료 포함)를 세고 있어
                                                 실측에서 50명 대 5명으로 갈렸다.
                                            */}
                                            <span style={{ fontSize: 12.5, color: '#7C8794', marginTop: 3 }}>
                                                활성 {c.activeMembers}명 · 수업 {c.lessons}회 · 미사용 선수금{' '}
                                                {won(c.portfolioValue)}원
                                            </span>
                                        </div>
                                        <span
                                            className="rounded-[8px]"
                                            style={{
                                                background: '#F1F5FC', color: '#2C4C8C',
                                                fontSize: 13, fontWeight: 700, padding: '7px 13px', flex: 'none',
                                            }}
                                        >
                                            리포트 열기
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

        </div>

        {/*
          리포트 본체는 CoachReportSheet 하나만 씁니다.
          강사 목록 행의 '리포트' 버튼도 같은 컴포넌트를 열어,
          어느 문으로 들어와도 같은 종이가 나옵니다.

          🔴 **배경 div 밖에 둔다** (2026-09-03 대표 제보).
             예전에는 배경 div 의 자식이었다. 시트는 `document.body` 로 포털을
             쓰지만 **React 합성 이벤트는 React 트리를 타고 오른다** — 그래서
             리포트 안의 「PDF 다운로드」를 누른 클릭이 위 배경의
             `onClick={onClose}` 까지 올라가 팝업이 통째로 닫혔다. 그러고 나서
             폰트 대기가 끝나 인쇄가 열리면 리포트는 이미 사라진 뒤라,
             **브리핑 화면이 인쇄됐다.**

             CoachPdfReport 쪽에서도 stopPropagation 으로 막지만, 원인은 이
             중첩이므로 여기서도 풀어 둔다. 방어는 두 겹이어야 한다 —
             한쪽은 언제든 다른 사람이 지운다.
        */}
        {picked && (
            <CoachReportSheet coach={picked} month={month} onClose={() => setPicked(null)} />
        )}
        </>
    );
};

export default CoachReportModal;
